import { z } from 'zod';
/**
 * Phase 5 — power grid.
 *
 * Every non-generator machine has two power-draw numbers: an `idleDrawKw`
 * baseline (always on, even between cycles) and an `activeDrawKw` surcharge
 * that adds while a recipe is mid-cycle. Generators post positive supply
 * (`generationKw`), capacitors buffer between ticks (`capacitorKwh`).
 *
 * The server computes a single `PowerBudget` per world each tick:
 *
 *   1. Sum `generationKw` across every healthy generator this tick.
 *   2. Sum `idle/active` draw across every consumer this tick.
 *   3. If demand > generation, try to discharge the capacitor bank to cover
 *      the gap. The world tracks total `capacitorStored` kWh across every
 *      capacitor-array machine.
 *   4. If there's still a gap (capacitor empty), compute
 *      `throttleRatio = gen / demand` ∈ [0, 1). Every consumer's cycle
 *      advances at `throttleRatio` of its usual speed — that's the "brownout".
 *   5. Any surplus (gen > demand) charges the capacitor bank until its cap.
 *
 * Clients get the `PowerBudget` on every snapshot to render the Power Panel:
 * generation vs demand bars, capacitor gauge, brownout flag.
 */
/**
 * P19A — pole link descriptor for canvas rendering. Each entry is an
 * undirected edge between two poles that are within link range AND
 * belong to the same connected network. Server computes once per tick;
 * client draws gold lines along these edges.
 */
export const PoleLinkSchema = z.object({
    a: z.string(),
    b: z.string(),
});
export const PowerBudgetSchema = z.object({
    /** Total kW produced this tick (solar + fuelled combustion cells, etc.). */
    generation: z.number().nonnegative(),
    /** Total kW demanded this tick (sum of idle + active draws across consumers). */
    demand: z.number().nonnegative(),
    /** Current kWh held in the combined capacitor bank. */
    capacitorStored: z.number().nonnegative(),
    /** Total kWh the bank can hold (sum of every capacitor array's `capacitorKwh`). */
    capacitorCapacity: z.number().nonnegative(),
    /**
     * [0, 1] — what fraction of the draw consumers actually received. 1 = all
     * machines ran full speed. 0.5 = everything ran at half speed this tick.
     */
    throttleRatio: z.number().min(0).max(1),
    /** True when `throttleRatio < 1` — convenience flag for the UI. */
    brownout: z.boolean(),
    /**
     * P19A — undirected edges between pole pairs in the same network.
     * Optional for legacy snapshot back-compat; the client treats absent
     * as `[]` (no power-line overlay). Used only for rendering — sim
     * truth is the per-machine `unpoweredMachineIds` list.
     */
    poleLinks: z.array(PoleLinkSchema).optional(),
    /**
     * P19A — ids of machines that are NOT in coverage of any networked
     * pole this tick. The renderer overlays them in slate to signal "no
     * power" without tinting every covered machine. Optional for legacy
     * compat; absent ⇒ no spatial gating (every machine considered
     * covered, matching pre-P19A behavior).
     */
    unpoweredMachineIds: z.array(z.string()).optional(),
});
/** Initial zero-activity budget. Broadcast before any tick has run. */
export function emptyPowerBudget() {
    return {
        generation: 0,
        demand: 0,
        capacitorStored: 0,
        capacitorCapacity: 0,
        throttleRatio: 1,
        brownout: false,
    };
}
/**
 * Marker values used by client chrome — the Power Panel flips from amber to
 * red when the ratio dips below this threshold, and shows a "CAPACITOR LOW"
 * badge below this.
 */
export const BROWNOUT_AMBER_RATIO = 0.85;
export const CAPACITOR_LOW_FRACTION = 0.2;
//# sourceMappingURL=power.js.map